import { v4 } from 'uuid';
import {
  ChatActionData,
  ChatActionType,
  ChatActionStatus,
  type ChatLoadedResourceType,
  type InterruptableToolDefinition,
} from './types';

export type SerializedChatAction = ChatActionData;

/**
 * Base abstract class for chat actions.
 * Provides common functionality for all action types.
 */
export abstract class ChatAction {
  protected _data: ChatActionData;

  constructor(data: ChatActionData) {
    this._data = { ...data };
  }

  /**
   * Get the action ID.
   */
  get id(): string {
    return this._data.id;
  }

  /**
   * Get the action type.
   */
  get type(): ChatActionType {
    return this._data.type;
  }

  /**
   * Get the action status.
   */
  get status(): ChatActionStatus {
    return this._data.status;
  }

  /**
   * Get the action data.
   */
  get data(): Record<string, unknown> | null {
    return this._data.data;
  }

  /**
   * Get the full action data object.
   */
  get actionData(): ChatActionData {
    return { ...this._data };
  }

  /**
   * Check if the action is pending.
   */
  isPending(): boolean {
    return this._data.status === 'pending';
  }

  /**
   * Check if the action is approved.
   */
  isApproved(): boolean {
    return this._data.status === 'approved';
  }

  /**
   * Check if the action is denied.
   */
  isDenied(): boolean {
    return this._data.status === 'denied';
  }

  /**
   * Check if the action is completed.
   */
  isCompleted(): boolean {
    return this._data.status === 'completed';
  }

  /**
   * Type guard: Check if this is a ToolApprovalAction.
   */
  isToolApprovalAction(): this is ToolApprovalAction {
    return this._data.type === 'tool_approval';
  }

  /**
   * Type guard: Check if this is a MessageQueueAction.
   */
  isMessageQueueAction(): this is MessageQueueAction {
    return this._data.type === 'message_queue';
  }

  /**
   * Type guard: Check if this is an AddToolAction.
   */
  isAddToolAction(): this is AddToolAction {
    return this._data.type === 'add_tool';
  }

  /**
   * Type guard: Check if this is a RemoveToolAction.
   */
  isRemoveToolAction(): this is RemoveToolAction {
    return this._data.type === 'remove_tool';
  }

  /**
   * Type guard: Check if this is a LoadResourceAction.
   */
  isLoadResourceAction(): this is LoadResourceAction {
    return this._data.type === 'load_resource';
  }

  /**
   * Type guard: Check if this is a LoadSkillAction.
   */
  isLoadSkillAction(): this is LoadSkillAction {
    return this._data.type === 'load_skill';
  }

  /**
   * Type guard: Check if this is an UnloadResourceAction.
   */
  isUnloadResourceAction(): this is UnloadResourceAction {
    return this._data.type === 'unload_resource';
  }

  /**
   * Type guard: Check if this is a LoadArtifactAction.
   */
  isLoadArtifactAction(): this is LoadArtifactAction {
    return this._data.type === 'load_artifact';
  }

  /**
   * Type guard: Check if this is a RemoveArtifactAction.
   */
  isRemoveArtifactAction(): this is RemoveArtifactAction {
    return this._data.type === 'remove_artifact';
  }

  /**
   * Type guard: Check if this is a FocusAgentAction.
   */
  isFocusAgentAction(): this is FocusAgentAction {
    return this._data.type === 'focus_agent';
  }

  /**
   * Type guard: Check if this is an UnfocusAgentAction.
   */
  isUnfocusAgentAction(): this is UnfocusAgentAction {
    return this._data.type === 'unfocus_agent';
  }

  /**
   * Type guard: Check if this is an OutputShapeAction.
   */
  isOutputShapeAction(): this is OutputShapeAction {
    return this._data.type === 'output_shape';
  }

  /**
   * Type guard: Check if this is an AutoApproveAction.
   */
  isAutoApproveAction(): this is AutoApproveAction {
    return this._data.type === 'auto_approve';
  }

  /**
   * Type guard: Check if this is a RemoveAutoApproveAction.
   */
  isRemoveAutoApproveAction(): this is RemoveAutoApproveAction {
    return this._data.type === 'remove_auto_approve';
  }

  /**
   * Type guard: Check if this is a RegisterInterruptableToolAction.
   */
  isRegisterInterruptableToolAction(): this is RegisterInterruptableToolAction {
    return this._data.type === 'register_interruptable_tool';
  }

  /**
   * Type guard: Check if this is an EnterPlanModeAction.
   */
  isEnterPlanModeAction(): this is EnterPlanModeAction {
    return this._data.type === 'enter_plan_mode';
  }

  /**
   * Type guard: Check if this is an ExitPlanModeAction.
   */
  isExitPlanModeAction(): this is ExitPlanModeAction {
    return this._data.type === 'exit_plan_mode';
  }

  /**
   * Approve this action.
   */
  approve(approvedBy: string): void {
    this._data.status = 'approved';
    this._data.approvedAt = new Date();
    this._data.approvedBy = approvedBy;
    this._data.updatedAt = new Date();
  }

  /**
   * Deny this action.
   */
  deny(deniedBy: string, reason?: string): void {
    const updatedData = this._data.data
      ? { ...this._data.data, ...(reason ? { deniedReason: reason } : {}) }
      : reason
      ? { deniedReason: reason }
      : {};

    this._data.status = 'denied';
    this._data.approvedAt = new Date();
    this._data.approvedBy = deniedBy;
    this._data.data = updatedData;
    this._data.updatedAt = new Date();
  }

  /**
   * Mark this action as completed.
   */
  complete(): void {
    this._data.status = 'completed';
    this._data.completedAt = new Date();
    this._data.updatedAt = new Date();
  }

  /**
   * Serialize this action to a plain object.
   */
  serialize(): SerializedChatAction {
    return { ...this._data };
  }

  /**
   * Create a clone of this action.
   */
  abstract clone(): ChatAction;

  /**
   * Factory function to create the appropriate action from data.
   */
  static fromData(data: ChatActionData): ChatAction {
    switch (data.type) {
      case ChatActionType.ToolApproval:
        return new ToolApprovalAction(data);
      case ChatActionType.MessageQueue:
        return new MessageQueueAction(data);
      case ChatActionType.AddTool:
        return new AddToolAction(data);
      case ChatActionType.RemoveTool:
        return new RemoveToolAction(data);
      case ChatActionType.LoadResource:
        return new LoadResourceAction(data);
      case ChatActionType.LoadSkill:
        return new LoadSkillAction(data);
      case ChatActionType.UnloadResource:
        return new UnloadResourceAction(data);
      case ChatActionType.InterruptedToolDataProvided:
        return new InterruptedToolDataProvidedAction(data);
      case ChatActionType.InterruptedToolInput:
        return new InterruptedToolInputAction(data);
      case ChatActionType.LoadArtifact:
        return new LoadArtifactAction(data);
      case ChatActionType.RemoveArtifact:
        return new RemoveArtifactAction(data);
      case ChatActionType.FocusAgent:
        return new FocusAgentAction(data);
      case ChatActionType.UnfocusAgent:
        return new UnfocusAgentAction(data);
      case ChatActionType.OutputShape:
        return new OutputShapeAction(data);
      case ChatActionType.AutoApprove:
        return new AutoApproveAction(data);
      case ChatActionType.RemoveAutoApprove:
        return new RemoveAutoApproveAction(data);
      case ChatActionType.EnterPlanMode:
        return new EnterPlanModeAction(data);
      case ChatActionType.ExitPlanMode:
        return new ExitPlanModeAction(data);
      case ChatActionType.RegisterInterruptableTool:
        return new RegisterInterruptableToolAction(data);
      default:
        throw new Error(
          `Unknown action type: ${(data as { type: string }).type}`
        );
    }
  }

  /**
   * Factory function to create the appropriate action from serialized data.
   */
  static fromSerialized(serialized: SerializedChatAction): ChatAction {
    return ChatAction.fromData(serialized);
  }
}

/**
 * Model for tool approval actions.
 */
export class ToolApprovalAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'tool_approval') {
      throw new Error('Invalid action type for ToolApprovalAction');
    }
  }

  /**
   * Get the tool key.
   */
  getToolKey(): string {
    if (!this._data.data || typeof this._data.data['toolKey'] !== 'string') {
      throw new Error('Tool key not found in tool approval action');
    }
    return this._data.data['toolKey'] as string;
  }

  /**
   * Get the tool call ID.
   */
  getToolCallId(): string | undefined {
    if (this._data.data && typeof this._data.data['toolCallId'] === 'string') {
      return this._data.data['toolCallId'] as string;
    }
    return undefined;
  }

  /**
   * Get the tool input.
   */
  getToolInput(): Record<string, unknown> {
    if (!this._data.data || typeof this._data.data['toolInput'] !== 'object') {
      throw new Error('Tool input not found in tool approval action');
    }
    return this._data.data['toolInput'] as Record<string, unknown>;
  }

  /**
   * Create a new tool approval action.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    toolKey: string;
    toolCallId: string;
    toolInput: Record<string, unknown>;
  }): ToolApprovalAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),

      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.ToolApproval,
      status: 'pending',
      data: {
        toolKey: params.toolKey,
        toolCallId: params.toolCallId,
        toolInput: params.toolInput,
      },
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new ToolApprovalAction(action);
  }

  /**
   * Create a ToolApprovalAction from serialized data.
   */
  static override fromSerialized(
    serialized: SerializedChatAction
  ): ToolApprovalAction {
    return new ToolApprovalAction(serialized);
  }

  clone(): ToolApprovalAction {
    return new ToolApprovalAction({ ...this._data });
  }
}

export class InterruptedToolInputAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'interrupted_tool_input') {
      throw new Error('Invalid action type for InterruptedToolInputAction');
    }
  }

  override clone(): ChatAction {
    return new InterruptedToolInputAction({ ...this._data });
  }

  getToolCallId(): string {
    if (!this._data.data || typeof this._data.data['toolCallId'] !== 'string') {
      throw new Error(
        'Tool call ID not found in interrupted tool input action'
      );
    }
    return this._data.data['toolCallId'] as string;
  }

  getToolInput(): Record<string, unknown> {
    if (!this._data.data || typeof this._data.data['toolInput'] !== 'object') {
      throw new Error('Tool input not found in interrupted tool input action');
    }
    return this._data.data['toolInput'] as Record<string, unknown>;
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    toolCallId: string;
    toolInput: Record<string, unknown>;
  }): InterruptedToolInputAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),

      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.InterruptedToolInput,
      status: 'completed',
      data: {
        toolCallId: params.toolCallId,
        toolInput: params.toolInput,
      },
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new InterruptedToolInputAction(action);
  }
}

export class InterruptedToolDataProvidedAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'interrupted_tool_data_provided') {
      throw new Error(
        'Invalid action type for InterruptedToolDataProvidedAction'
      );
    }
  }

  getToolCallId(): string {
    if (!this._data.data || typeof this._data.data['toolCallId'] !== 'string') {
      throw new Error(
        'Tool call ID not found in interrupted tool data provided action'
      );
    }
    return this._data.data['toolCallId'] as string;
  }

  getToolInput(): Record<string, unknown> {
    if (!this._data.data || typeof this._data.data['toolInput'] !== 'object') {
      throw new Error(
        'Tool input not found in interrupted tool data provided action'
      );
    }
    return this._data.data['toolInput'] as Record<string, unknown>;
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    toolCallId: string;
    toolInput: Record<string, unknown>;
  }): InterruptedToolDataProvidedAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),

      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.InterruptedToolDataProvided,
      status: 'completed',
      data: {
        toolCallId: params.toolCallId,
        toolInput: params.toolInput,
      },
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new InterruptedToolDataProvidedAction(action);
  }

  static override fromSerialized(
    serialized: SerializedChatAction
  ): InterruptedToolDataProvidedAction {
    return new InterruptedToolDataProvidedAction(serialized);
  }

  clone(): InterruptedToolDataProvidedAction {
    return new InterruptedToolDataProvidedAction({ ...this._data });
  }
}

/**
 * Model for message queue actions.
 */
export class MessageQueueAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'message_queue') {
      throw new Error('Invalid action type for MessageQueueAction');
    }
  }

  /**
   * Get the message content.
   */
  getMessageContent(): string {
    if (
      !this._data.data ||
      typeof this._data.data['messageContent'] !== 'string'
    ) {
      throw new Error('Message content not found in message queue action');
    }
    return this._data.data['messageContent'] as string;
  }

  /**
   * Get the message files.
   */
  getMessageFiles(): unknown[] | undefined {
    if (!this._data.data || !('messageFiles' in this._data.data)) {
      return undefined;
    }
    return this._data.data['messageFiles'] as unknown[] | undefined;
  }

  /**
   * Create a new message queue action.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    messageContent: string;
    messageFiles?: unknown[];
  }): MessageQueueAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),

      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.MessageQueue,
      status: 'pending',
      data: {
        messageContent: params.messageContent,
        messageFiles: params.messageFiles,
      },
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new MessageQueueAction(action);
  }

  /**
   * Create a MessageQueueAction from serialized data.
   */
  static override fromSerialized(
    serialized: SerializedChatAction
  ): MessageQueueAction {
    return new MessageQueueAction(serialized);
  }

  clone(): MessageQueueAction {
    return new MessageQueueAction({ ...this._data });
  }
}

/**
 * Model for add_tool actions.
 */
export class AddToolAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'add_tool') {
      throw new Error('Invalid action type for AddToolAction');
    }
  }

  /**
   * Get the tool key to add.
   */
  getToolKey(): string {
    if (
      !this._data.data ||
      typeof this._data.data['toolKeyToAdd'] !== 'string'
    ) {
      throw new Error('Tool key not found in add_tool action');
    }
    return this._data.data['toolKeyToAdd'] as string;
  }

  /**
   * Get the tool call ID (optional).
   */
  getToolCallId(): string | undefined {
    if (!this._data.data || !('toolCallId' in this._data.data)) {
      return undefined;
    }
    return this._data.data['toolCallId'] as string | undefined;
  }

  /**
   * Create a new add_tool action.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    toolKey: string;
    toolCallId?: string;
  }): AddToolAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.AddTool,
      status: 'pending',
      data: {
        toolKeyToAdd: params.toolKey,
        toolCallId: params.toolCallId,
      },
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new AddToolAction(action);
  }

  /**
   * Create an AddToolAction from serialized data.
   */
  static override fromSerialized(
    serialized: SerializedChatAction
  ): AddToolAction {
    return new AddToolAction(serialized);
  }

  clone(): AddToolAction {
    return new AddToolAction({ ...this._data });
  }
}

/**
 * Model for remove_tool actions.
 */
export class RemoveToolAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'remove_tool') {
      throw new Error('Invalid action type for RemoveToolAction');
    }
  }

  /**
   * Get the tool key to remove.
   */
  getToolKey(): string {
    if (
      !this._data.data ||
      typeof this._data.data['toolKeyToRemove'] !== 'string'
    ) {
      throw new Error('Tool key not found in remove_tool action');
    }
    return this._data.data['toolKeyToRemove'] as string;
  }

  /**
   * Get the reason for removal (optional).
   */
  getReason(): string | undefined {
    if (!this._data.data || !('reason' in this._data.data)) {
      return undefined;
    }
    return this._data.data['reason'] as string | undefined;
  }

  /**
   * Create a new remove_tool action.
   * @param status - 'pending' requires approval; 'completed' takes effect immediately (e.g. user-initiated unload).
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    toolKey: string;
    reason?: string;
    status?: 'pending' | 'completed';
  }): RemoveToolAction {
    const now = new Date();
    const status = params.status ?? 'pending';
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.RemoveTool,
      status,
      data: {
        toolKeyToRemove: params.toolKey,
        reason: params.reason,
      },
      completedAt: status === 'completed' ? now : null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new RemoveToolAction(action);
  }

  /**
   * Create a RemoveToolAction from serialized data.
   */
  static override fromSerialized(
    serialized: SerializedChatAction
  ): RemoveToolAction {
    return new RemoveToolAction(serialized);
  }

  clone(): RemoveToolAction {
    return new RemoveToolAction({ ...this._data });
  }
}

const CHAT_LOADED_RESOURCE_TYPES: readonly ChatLoadedResourceType[] = [
  'chat',
  'agent',
  'folder',
  'trigger',
  'workflow',
] as const;

function isChatLoadedResourceType(
  value: string
): value is ChatLoadedResourceType {
  return (CHAT_LOADED_RESOURCE_TYPES as readonly string[]).includes(value);
}

/**
 * Model for load_resource actions (resource contract: chat, agent, folder, trigger).
 */
export class LoadResourceAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'load_resource') {
      throw new Error('Invalid action type for LoadResourceAction');
    }
  }

  getResourceType(): ChatLoadedResourceType {
    const raw = this._data.data?.['resourceType'];
    if (typeof raw !== 'string' || !isChatLoadedResourceType(raw)) {
      throw new Error(
        'resourceType not found or invalid in load_resource action'
      );
    }
    return raw;
  }

  getResourceId(): string {
    const raw = this._data.data?.['resourceId'];
    if (typeof raw !== 'string') {
      throw new Error('resourceId not found in load_resource action');
    }
    return raw;
  }

  getSnapshot(): unknown | undefined {
    const d = this._data.data;
    if (d === null || d === undefined || !('snapshot' in d)) {
      return undefined;
    }
    return d['snapshot'];
  }

  tryGetDescriptor(): {
    resourceType: ChatLoadedResourceType;
    resourceId: string;
    snapshot: unknown | undefined;
  } | null {
    const d = this._data.data;
    if (d === null || d === undefined) {
      return null;
    }
    const resourceType = d['resourceType'];
    const resourceId = d['resourceId'];
    if (
      typeof resourceType !== 'string' ||
      !isChatLoadedResourceType(resourceType) ||
      typeof resourceId !== 'string'
    ) {
      return null;
    }
    const snapshot = 'snapshot' in d ? d['snapshot'] : undefined;
    return { resourceType, resourceId, snapshot };
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    resourceType: ChatLoadedResourceType;
    resourceId: string;
    snapshot?: unknown;
  }): LoadResourceAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.LoadResource,
      status: 'completed',
      data: {
        resourceType: params.resourceType,
        resourceId: params.resourceId,
        ...(params.snapshot !== undefined ? { snapshot: params.snapshot } : {}),
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new LoadResourceAction(action);
  }

  clone(): LoadResourceAction {
    return new LoadResourceAction({ ...this._data });
  }
}

/**
 * Model for load_skill actions (developer-defined Coco skills).
 */
export class LoadSkillAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'load_skill') {
      throw new Error('Invalid action type for LoadSkillAction');
    }
  }

  getSkillKey(): string {
    const raw = this._data.data?.['skillKey'];
    if (typeof raw !== 'string') {
      throw new Error('skillKey not found in load_skill action');
    }
    return raw;
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    skillKey: string;
  }): LoadSkillAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.LoadSkill,
      status: 'completed',
      data: {
        skillKey: params.skillKey.trim(),
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new LoadSkillAction(action);
  }

  clone(): LoadSkillAction {
    return new LoadSkillAction({ ...this._data });
  }
}

/**
 * Model for unload_resource actions.
 */
export class UnloadResourceAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'unload_resource') {
      throw new Error('Invalid action type for UnloadResourceAction');
    }
  }

  getResourceType(): ChatLoadedResourceType {
    const raw = this._data.data?.['resourceType'];
    if (typeof raw !== 'string' || !isChatLoadedResourceType(raw)) {
      throw new Error(
        'resourceType not found or invalid in unload_resource action'
      );
    }
    return raw;
  }

  getResourceId(): string {
    const raw = this._data.data?.['resourceId'];
    if (typeof raw !== 'string') {
      throw new Error('resourceId not found in unload_resource action');
    }
    return raw;
  }

  /**
   * @param status - Like remove_tool: `completed` applies immediately when persisting unload.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    resourceType: ChatLoadedResourceType;
    resourceId: string;
    status?: 'pending' | 'completed';
  }): UnloadResourceAction {
    const now = new Date();
    const status = params.status ?? 'completed';
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.UnloadResource,
      status,
      data: {
        resourceType: params.resourceType,
        resourceId: params.resourceId,
      },
      completedAt: status === 'completed' ? now : null,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new UnloadResourceAction(action);
  }

  clone(): UnloadResourceAction {
    return new UnloadResourceAction({ ...this._data });
  }
}

/**
 * Model for load_artifact actions.
 * Used to programmatically add artifacts to a conversation (e.g. from command palette).
 */
export class LoadArtifactAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'load_artifact') {
      throw new Error('Invalid action type for LoadArtifactAction');
    }
  }

  /**
   * Get the artifact type.
   */
  getArtifactType(): string {
    if (
      !this._data.data ||
      typeof this._data.data['artifactType'] !== 'string'
    ) {
      throw new Error('Artifact type not found in load_artifact action');
    }
    return this._data.data['artifactType'] as string;
  }

  /**
   * Get the artifact ID.
   */
  getArtifactId(): string {
    if (!this._data.data || typeof this._data.data['artifactId'] !== 'string') {
      throw new Error('Artifact ID not found in load_artifact action');
    }
    return this._data.data['artifactId'] as string;
  }

  /**
   * Get the artifact data (payload).
   */
  getArtifactData(): unknown {
    if (!this._data.data || !('artifactData' in this._data.data)) {
      throw new Error('Artifact data not found in load_artifact action');
    }
    return this._data.data['artifactData'];
  }

  /**
   * Safely get artifact descriptor. Returns null if action data is incomplete
   * (e.g. from DB or stream). Use this when iterating over actions from
   * external sources to avoid uncaught exceptions.
   */
  tryGetArtifactDescriptor(): {
    type: string;
    artifactId: string;
    artifactData: unknown;
    toolCallId: string | undefined;
  } | null {
    const d = this._data.data;
    if (d === null || d === undefined) {
      return null;
    }
    if (typeof d['artifactType'] !== 'string') {
      return null;
    }
    if (typeof d['artifactId'] !== 'string') {
      return null;
    }
    if (!('artifactData' in d)) {
      return null;
    }
    return {
      type: d['artifactType'] as string,
      artifactId: d['artifactId'] as string,
      artifactData: d['artifactData'],
      toolCallId:
        typeof d['toolCallId'] === 'string' ? d['toolCallId'] : undefined,
    };
  }

  /**
   * Create a new load_artifact action.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    artifactType: string;
    artifactId: string;
    artifactData: unknown;
    toolCallId?: string;
  }): LoadArtifactAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.LoadArtifact,
      status: 'completed',
      data: {
        artifactType: params.artifactType,
        artifactId: params.artifactId,
        artifactData: params.artifactData,
        ...(params.toolCallId !== undefined
          ? { toolCallId: params.toolCallId }
          : {}),
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new LoadArtifactAction(action);
  }

  clone(): LoadArtifactAction {
    return new LoadArtifactAction({ ...this._data });
  }
}

/**
 * Model for remove_artifact actions — the user dismissed an artifact from the
 * panel. Suppresses that `artifactId` in {@link Conversation.artifacts} whatever
 * put it there (a `load_artifact` action, a tool output, a parent chat), so
 * closing a tab takes it out of the model's active context too.
 *
 * Dismissal is not permanent: anything that adds the same artifact *after* this
 * action brings it back, so an agent can re-open what the user closed.
 */
export class RemoveArtifactAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'remove_artifact') {
      throw new Error('Invalid action type for RemoveArtifactAction');
    }
  }

  /**
   * Get the artifact ID to dismiss.
   */
  getArtifactId(): string {
    const artifactId = this.tryGetArtifactId();
    if (artifactId === null) {
      throw new Error('Artifact ID not found in remove_artifact action');
    }
    return artifactId;
  }

  /**
   * Safely get the dismissed artifact ID. Returns null when the action data is
   * incomplete (e.g. from DB or stream), mirroring
   * {@link LoadArtifactAction.tryGetArtifactDescriptor}.
   */
  tryGetArtifactId(): string | null {
    const raw = this._data.data?.['artifactId'];
    return typeof raw === 'string' && raw !== '' ? raw : null;
  }

  /**
   * Create a new remove_artifact action. Always `completed`: dismissing an
   * artifact is a user gesture that takes effect immediately.
   */
  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    artifactId: string;
  }): RemoveArtifactAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.RemoveArtifact,
      status: 'completed',
      data: {
        artifactId: params.artifactId,
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new RemoveArtifactAction(action);
  }

  clone(): RemoveArtifactAction {
    return new RemoveArtifactAction({ ...this._data });
  }
}

/**
 * Model for focus_agent actions.
 * When present, the LLM should prioritize using this subagent for tasks.
 * Only one agent can be focused at a time; focusing a new agent replaces the previous focus.
 */
export class FocusAgentAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'focus_agent') {
      throw new Error('Invalid action type for FocusAgentAction');
    }
  }

  getAgentId(): string {
    if (!this._data.data || typeof this._data.data['agentId'] !== 'string') {
      throw new Error('Agent ID not found in focus_agent action');
    }
    return this._data.data['agentId'] as string;
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    agentId: string;
  }): FocusAgentAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.FocusAgent,
      status: 'completed',
      data: {
        agentId: params.agentId,
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new FocusAgentAction(action);
  }

  clone(): FocusAgentAction {
    return new FocusAgentAction({ ...this._data });
  }
}

/**
 * Model for unfocus_agent actions.
 * Clears the focused agent so the LLM no longer prioritizes any specific subagent.
 */
export class UnfocusAgentAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'unfocus_agent') {
      throw new Error('Invalid action type for UnfocusAgentAction');
    }
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
  }): UnfocusAgentAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.UnfocusAgent,
      status: 'completed',
      data: null,
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new UnfocusAgentAction(action);
  }

  clone(): UnfocusAgentAction {
    return new UnfocusAgentAction({ ...this._data });
  }
}

/**
 * Records the expected structured output shape (EraQL string) for the chat.
 * The latest completed {@link OutputShapeAction} wins; see {@link Conversation.outputShape}.
 */
export class OutputShapeAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'output_shape') {
      throw new Error('Invalid action type for OutputShapeAction');
    }
  }

  getShape(): string {
    if (!this._data.data || typeof this._data.data['shape'] !== 'string') {
      throw new Error('shape not found in output_shape action');
    }
    return this._data.data['shape'] as string;
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    shape: string;
  }): OutputShapeAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.OutputShape,
      status: 'completed',
      data: {
        shape: params.shape,
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new OutputShapeAction(action);
  }

  clone(): OutputShapeAction {
    return new OutputShapeAction({ ...this._data });
  }
}

/**
 * Model for auto_approve actions.
 * When present, tool calls are automatically approved without manual intervention.
 */
export class AutoApproveAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'auto_approve') {
      throw new Error('Invalid action type for AutoApproveAction');
    }
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
  }): AutoApproveAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.AutoApprove,
      status: 'completed',
      data: null,
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new AutoApproveAction(action);
  }

  clone(): AutoApproveAction {
    return new AutoApproveAction({ ...this._data });
  }
}

/**
 * Model for remove_auto_approve actions.
 * Reverts to manual approval mode where tool calls require user approval.
 */
export class RemoveAutoApproveAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'remove_auto_approve') {
      throw new Error('Invalid action type for RemoveAutoApproveAction');
    }
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
  }): RemoveAutoApproveAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.RemoveAutoApprove,
      status: 'completed',
      data: null,
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new RemoveAutoApproveAction(action);
  }

  clone(): RemoveAutoApproveAction {
    return new RemoveAutoApproveAction({ ...this._data });
  }
}

/**
 * Model for enter_plan_mode actions.
 * When the most recent plan-mode action is this, the conversation is in plan mode:
 * architecture skills load and Coco is instructed to plan before building.
 */
export class EnterPlanModeAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'enter_plan_mode') {
      throw new Error('Invalid action type for EnterPlanModeAction');
    }
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
  }): EnterPlanModeAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.EnterPlanMode,
      status: 'completed',
      data: null,
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new EnterPlanModeAction(action);
  }

  clone(): EnterPlanModeAction {
    return new EnterPlanModeAction({ ...this._data });
  }
}

/**
 * Model for register_interruptable_tool actions. Records an ad-hoc
 * client-implemented tool definition ({@link InterruptableToolDefinition}) on
 * the conversation so the server can render it to the LLM. The latest
 * completed action per toolKey wins.
 */
export class RegisterInterruptableToolAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'register_interruptable_tool') {
      throw new Error(
        'Invalid action type for RegisterInterruptableToolAction'
      );
    }
  }

  getToolKey(): string {
    const raw = this._data.data?.['toolKey'];
    if (typeof raw !== 'string') {
      throw new Error(
        'toolKey not found in register_interruptable_tool action'
      );
    }
    return raw;
  }

  /** Safely extract the stored definition; returns null when action data is incomplete. */
  tryGetDefinition(): InterruptableToolDefinition | null {
    const d = this._data.data;
    if (d === null || d === undefined) {
      return null;
    }
    const toolKey = d['toolKey'];
    const name = d['name'];
    const description = d['description'];
    const inputSchema = d['inputSchema'];
    if (
      typeof toolKey !== 'string' ||
      typeof name !== 'string' ||
      typeof description !== 'string' ||
      inputSchema === null ||
      typeof inputSchema !== 'object' ||
      Array.isArray(inputSchema)
    ) {
      return null;
    }
    return {
      toolKey,
      name,
      description,
      inputSchema: inputSchema as Record<string, unknown>,
    };
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
    definition: InterruptableToolDefinition;
  }): RegisterInterruptableToolAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.RegisterInterruptableTool,
      status: 'completed',
      data: {
        toolKey: params.definition.toolKey,
        name: params.definition.name,
        description: params.definition.description,
        inputSchema: params.definition.inputSchema,
      },
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new RegisterInterruptableToolAction(action);
  }

  clone(): RegisterInterruptableToolAction {
    return new RegisterInterruptableToolAction({ ...this._data });
  }
}

/**
 * Model for exit_plan_mode actions. Turns plan mode off.
 */
export class ExitPlanModeAction extends ChatAction {
  constructor(data: ChatActionData) {
    super(data);
    if (data.type !== 'exit_plan_mode') {
      throw new Error('Invalid action type for ExitPlanModeAction');
    }
  }

  static create(params: {
    orgId: string;
    actorId: string;
    chatId: string;
  }): ExitPlanModeAction {
    const now = new Date();
    const action: ChatActionData = {
      id: v4(),
      actorId: params.actorId,
      chatId: params.chatId,
      type: ChatActionType.ExitPlanMode,
      status: 'completed',
      data: null,
      completedAt: now,
      approvedAt: null,
      approvedBy: null,
      createdAt: now,
      updatedAt: now,
    };
    return new ExitPlanModeAction(action);
  }

  clone(): ExitPlanModeAction {
    return new ExitPlanModeAction({ ...this._data });
  }
}
